import { BRAND } from "@/lib/brand";
import { splitSavedQuote } from "@/lib/quoteText";

// Shop block from settings (used on printed / shared quotes)
export const shopFromSettings = (s) => ({
  name: s?.shop_name || BRAND.fullName,
  tagline: BRAND.tagline,
  address: s?.shop_address || "",
  phone: s?.shop_phone || "",
  email: s?.shop_email || "",
  footer: s?.quote_footer || "",
  invoiceFooter: s?.invoice_footer || "",
  gstRegNo: s?.gst_reg_no || "",
  bankDetails: s?.bank_details || "",
  paynowUen: s?.paynow_uen || "",
  sym: s?.currency_symbol || "$",
  countryCode: s?.phone_country_code || "65",
  bays: Number(s?.workshop_bays) || 0,
  ownerWhatsapp: s?.owner_whatsapp || "",
});

const shortRef = (id) => (id ? `HLH-${String(id).replace(/-/g, "").slice(0, 6).toUpperCase()}` : "DRAFT");

// One shape for both live (unsaved) and saved quotes
export function docFromLive({ quote, size, fields, shop, mode, ref }) {
  return {
    ref: ref || "DRAFT",
    created_at: new Date().toISOString(),
    shop,
    customer: { name: fields.customer, phone: fields.phone },
    vehicle: { plate: fields.plate.toUpperCase(), model: fields.vehicle, paintCode: fields.paintCode.toUpperCase() },
    size,
    manual: quote.manual,
    mult: quote.mult,
    panels: quote.lineItems,
    services: quote.serviceItems,
    parts: quote.partItems || [],
    breakdown: { respray: quote.respraySubtotal, repair: quote.repairSubtotal, special: quote.specialSubtotal, services: quote.servicesSubtotal, parts: quote.partsSubtotal },
    subtotal: quote.subtotal,
    discount: { mode: quote.discountMode, value: quote.discountValue, amount: quote.discountAmount },
    taxRate: quote.taxRate,
    tax: quote.tax,
    total: quote.total,
    notes: "",
  };
}

export function docFromSaved(qt, shop) {
  const { panels, services } = splitSavedQuote(qt);
  const b = qt.breakdown || {};
  return {
    ref: qt.quote_no || shortRef(qt.id),
    created_at: qt.created_at,
    status: qt.status,
    shop,
    customer: { name: qt.customer_name || "", phone: qt.customer_phone || "" },
    vehicle: { plate: qt.plate || "", model: qt.vehicle || "", paintCode: qt.paint_code || "" },
    size: qt.size,
    manual: true,
    panels,
    services,
    parts: qt.parts || [],
    breakdown: { respray: b.respray, repair: b.repair, special: b.special, services: b.services, parts: b.parts },
    subtotal: qt.subtotal,
    discount: qt.discount || { amount: 0 },
    taxRate: b.tax_rate,
    tax: qt.tax,
    total: qt.total,
    notes: qt.notes || "",
  };
}

export const pdfFilename = (doc) => {
  if (doc.kind === "soa") return doc.filename;
  if (doc.kind === "statement") return `Statement_${(doc.customer.name || "customer").replace(/[^\w\u4e00-\u9fff-]+/g, "_")}_${doc.statementDate}.pdf`;
  const who = doc.vehicle.plate || doc.customer.name || "walk-in";
  const date = (doc.created_at || "").slice(0, 10);
  const ref = doc.ref && doc.ref !== "DRAFT" ? `${doc.ref}_` : "";
  return `${ref}${doc.kind === "invoice" ? "Invoice" : "Quote"}_${who.replace(/[^A-Za-z0-9]+/g, "-")}_${date}.pdf`;
};

export function docFromInvoice(inv, shop) {
  const b = inv.breakdown || {};
  return {
    kind: "invoice",
    ref: inv.invoice_no,
    quoteRef: inv.quote_no,
    created_at: inv.issue_date,
    dueDate: inv.due_date,
    termsDays: inv.terms_days,
    status: inv.status,
    paidAt: inv.paid_at,
    shop,
    customer: { name: inv.company || inv.customer_name || "", phone: inv.customer_phone || "", contact: inv.contact_person || (inv.company ? inv.customer_name : ""), uen: inv.uen || "", address: inv.billing_address || "" },
    vehicle: { plate: inv.plate || "", model: inv.vehicle || "", paintCode: inv.paint_code || "" },
    size: inv.size,
    manual: true,
    panels: inv.line_items || [],
    services: inv.services || [],
    parts: inv.parts || [],
    breakdown: { respray: b.respray, repair: b.repair, special: b.special, services: b.services, parts: b.parts },
    subtotal: inv.subtotal,
    discount: inv.discount || { amount: 0 },
    taxRate: b.tax_rate,
    tax: inv.tax,
    total: inv.total,
    notes: inv.notes || "",
  };
}

export function docFromStatement(st, shop) {
  const c = st.customer;
  return {
    kind: "statement",
    shop,
    statementDate: st.statement_date,
    customer: { name: c.company || c.name, contact: c.contact_person || (c.company ? c.name : ""), uen: c.uen || "", address: c.billing_address || "", phone: c.phone || "", email: c.email || "", termsDays: c.payment_terms_days ?? 30 },
    invoices: st.invoices,
    totalDue: st.total_due,
    overdueDue: st.overdue_due,
  };
}
